import { useFormik } from "formik";
import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { RESET_SPECIFIC_CATEGORIES } from "../../store/Types/CategoryTypes";
import { useTranslation } from "react-i18next";
import { Calendar } from "primereact/calendar";
import {
  addCategory,
  getCategory,
} from "../../store/AsyncMethods/CategoryMethod";
import * as Yup from "yup";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { RESET_ERROR } from "../../store/Types/AuthTypes";
import { toast } from "react-toastify";
import { Divider } from "primereact/divider";
import {
  addExpense,
  getAllExpenses,
  getExpenseTypes,
} from "../../store/AsyncMethods/ExpenseMethods";
import moment from "moment";
import { Dropdown } from "primereact/dropdown";

export default function AddExpenses() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { error, user } = useSelector((state) => state.AuthReducer);
  const expensesDate = (date) =>
    date ? moment(date).format("YYYY-MM-DD") : null;

  let { expenseTypes } = useSelector((state) => state.expenseReducer);

  const updatedExpenseTypes = expenseTypes.map((item) => ({
    name: item.type,
    ...item,
  }));
  const formik = useFormik({
    initialValues: {
      expenses_date: new Date(),
      description: "",
      amount: 0,
      expense_type: null,
    },
    validationSchema: Yup.object({
      expenses_date: Yup.string().required("From Date Required"),
      description: Yup.string().required("Expense Required"),
      amount: Yup.number().required("Amount Required"),
    }),

    onSubmit: async (data) => {
      const dataObj = {
        expenses_date: expensesDate(data.expenses_date),
        description: data.description,
        amount: data.amount,
        created_by: user.id,
        expense_type: data.expense_type.id,
      };
      dispatch(addExpense(dataObj)).then((success) => {
        if (success) {
          formik.resetForm();
          dispatch(getAllExpenses());
          navigate(
            user?.role_id == 1 ? "/manage-expense" : "/categories-sales"
          );
        }
      });
    },
  });

  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch({ type: RESET_ERROR });
    }
  }, [error]);

  useEffect(() => {
    if (user) {
      dispatch(getExpenseTypes());
    }
  }, [dispatch, user]);

  return (
    <div className="mx-4">
      <div>
        <Button
          label={t("back")}
          icon="pi pi-arrow-left"
          className="p-black-btn"
          onClick={() => navigate(-1)}
        />
      </div>
      <form onSubmit={formik.handleSubmit}>
        <div className="card shadow-md rounded-lg p-4 mt-4">
          <Divider>
            <span className="text-2xl font-bold p-font-style text-center text-primary mx-1">
              {t("Add Expense")}
            </span>
          </Divider>
          <div className="p-4 gap-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            <div className="flex flex-col gap-2">
              <label htmlFor="expense_type">{t("Expense Type")}</label>
              <Dropdown
                id="expense_type"
                name="expense_type"
                value={formik.values.expense_type}
                onChange={(e) => {
                  formik.setFieldValue("expense_type", e.target.value);
                }}
                onBlur={formik.handleBlur}
                options={updatedExpenseTypes}
                optionLabel="name"
                filter
                placeholder="Select Expense Type"
                className="w-full p-primary-input"
                multiple={false}
              />
              {formik.touched.expense_type && formik.errors.expense_type && (
                <div className="p-error">{formik.errors.expense_type}</div>
              )}
            </div>
          </div>
          <div className="px-4 gap-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            <div className="flex flex-col gap-2">
              <label htmlFor="expenses_date">{t("Expenses Date")}</label>
              <Calendar
                id="expenses_date"
                name="expenses_date"
                value={
                  formik.values.expenses_date
                    ? new Date(formik.values.expenses_date)
                    : null
                }
                onChange={(e) =>
                  formik.setFieldValue("expenses_date", e.value || null)
                }
                onBlur={formik.handleBlur}
                className="w-full calender p-primary-cld-btn"
                showIcon
              />
              {formik.touched.expenses_date && formik.errors.expenses_date && (
                <div className="p-error">{formik.errors.expenses_date}</div>
              )}
            </div>
            <div className="">
              <div className="flex flex-col gap-2">
                <label htmlFor="description" className="">
                  {t("Expense Description")}
                </label>
                <span className="w-full">
                  <InputText
                    id="description"
                    name="description"
                    className="w-full text-lg p-primary-input"
                    value={formik.values.description}
                    onChange={formik.handleChange}
                  />
                </span>
              </div>
              {formik.touched?.description && formik.errors?.description && (
                <div className="p-error">{formik.errors?.description}</div>
              )}
            </div>
            <div className="">
              <div className="flex flex-col gap-2">
                <label htmlFor="amount" className="">
                  {t("Amount")}
                </label>
                <span className=" w-full">
                  <InputText
                    id="amount"
                    name="amount"
                    className="w-full text-lg p-primary-input"
                    value={formik.values.amount}
                    onChange={formik.handleChange}
                  />
                </span>
              </div>
              {formik.touched?.amount && formik.errors?.amount && (
                <div className="p-error">{formik.errors?.amount}</div>
              )}
            </div>
          </div>
          <div className="mt-16">
            <div className="flex justify-end gap-4">
              <Button
                label={t("cancel")}
                icon="pi pi-times"
                className="p-red-btn"
                type="button"
                onClick={() => {
                  formik.resetForm();
                  dispatch({ type: RESET_SPECIFIC_CATEGORIES });
                }}
              />
              <Button
                disabled={
                  !formik.values.amount ||
                  !formik.values.description ||
                  !formik.values.expenses_date
                }
                label={t("submit")}
                icon="pi pi-check"
                className="p-secondary-btn"
              />
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}
